import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api } from "../api/client";
import { OPERATOR } from "../../shared/constants";

// 顧客エリアの共通レイアウト。
export function CustomerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [caseStatus, setCaseStatus] = useState<string | null>(null);
  const [hasPublishedPage, setHasPublishedPage] = useState(false);
  const [sidebarNotice, setSidebarNotice] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get<{ case: { id: string; status: string } | null }>("/api/cases/current"),
      api.get<{ cases: Array<{ id: string; status: string }> }>("/api/cases"),
    ])
      .then(([res, casesRes]) => {
        const hasPublishedCase = casesRes.cases.some((item) => item.status === "published" || item.status === "improving");
        if (mounted) {
          setCaseStatus(res.case?.status ?? null);
          setHasPublishedPage(hasPublishedCase);
        }
        if (!hasPublishedCase && res.case?.id) {
          void api.get<{ os: { pages: Array<{ status: string }> } }>(`/api/cases/${res.case.id}/os`)
            .then((osRes) => {
              if (mounted) setHasPublishedPage(osRes.os.pages.some((page) => page.status === "published"));
            })
            .catch(() => {
              if (mounted) setHasPublishedPage(false);
            });
        }
      })
      .catch(() => {
        if (mounted) setCaseStatus(null);
      });
    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const initial = (user?.last_name || user?.email || "L").slice(0, 1);
  const onboarding = caseStatus !== "published" && caseStatus !== "improving";
  const steps = [
    { label: "初期費用のお支払い", path: "/payment" },
    { label: "事務所情報", path: "/hearing" },
    { label: "キーワード設計", path: "/keywords" },
    { label: "素材管理", path: "/assets" },
    { label: "ページ設計", path: "/page-plan" },
    { label: "公開", path: "/domain-guide" },
  ];
  const stepByStatus: Record<string, string> = {
    intake: "/hearing",
    knowledge_ready: "/keywords",
    strategy_ready: "/assets",
    page_plan_ready: "/page-plan",
    approved: "/page-plan",
    awaiting_payment: "/payment",
    in_production: "/page-plan",
  };
  const allowedOnboardingPaths = new Set(steps.map((step) => step.path));

  useEffect(() => {
    if (!onboarding || caseStatus === null) return;
    const allowed =
      caseStatus === "awaiting_payment"
        ? location.pathname.startsWith("/payment")
        : Array.from(allowedOnboardingPaths).some((path) => location.pathname.startsWith(path));
    if (!allowed) navigate(stepByStatus[caseStatus] ?? "/hearing", { replace: true });
  }, [onboarding, caseStatus, location.pathname]);

  useEffect(() => {
    if (onboarding || caseStatus === null) return;
    if (location.pathname.startsWith("/hearing")) {
      navigate("/dashboard", { replace: true });
    }
  }, [onboarding, caseStatus, location.pathname, navigate]);

  if (onboarding) {
    return (
      <div className="lp-onboarding-shell">
        <header className="lp-onboarding-header">
          <div className="lp-brand" aria-label={OPERATOR.serviceName}>
            <span className="lp-brand-mark">L</span>
            <span>{OPERATOR.serviceName}</span>
          </div>
          <div className="lp-onboarding-user">
            <strong>{user ? `${user.last_name} ${user.first_name}`.trim() || "ユーザー" : "ユーザー"}</strong>
            <span className="lp-user-avatar">{initial}</span>
            <button className="btn btn-ghost btn-sm" onClick={onLogout}>
              ログアウト
            </button>
          </div>
        </header>
        <main className="lp-onboarding-main">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="lp-shell">
      <aside className="lp-sidebar">
        <Link to="/dashboard" className="lp-brand" aria-label={OPERATOR.serviceName}>
          <span className="lp-brand-mark">L</span>
          <span>{OPERATOR.serviceName}</span>
        </Link>
        <nav className="lp-side-nav">
          <NavLink to="/dashboard" className={({ isActive }) => (isActive ? "active" : "")}>
            <span className="lp-nav-icon">⌂</span>
            ダッシュボード
          </NavLink>
          <NavLink to="/keywords" className={({ isActive }) => (isActive ? "active" : "")}>
            <span className="lp-nav-icon">⌕</span>
            キーワード戦略
          </NavLink>
          <NavLink to="/assets" className={({ isActive }) => (isActive ? "active" : "")}>
            <span className="lp-nav-icon">□</span>
            素材管理
          </NavLink>
          <NavLink to="/page-plan" className={({ isActive }) => (isActive ? "active" : "")}>
            <span className="lp-nav-icon">▤</span>
            ページ設計
          </NavLink>
          {hasPublishedPage ? (
            <>
              <NavLink to="/studio/blog" className={({ isActive }) => (isActive ? "active" : "")}>
                <span className="lp-nav-icon">✎</span>
                AIブログ
              </NavLink>
              <NavLink to="/contacts" className={({ isActive }) => (isActive ? "active" : "")}>
                <span className="lp-nav-icon">✉</span>
                お問い合わせ
              </NavLink>
            </>
          ) : (
            <>
              <button
                type="button"
                className="lp-side-disabled"
                onClick={() => setSidebarNotice("AIブログを使うには、先に少なくとも1ページを公開してください。")}
              >
                <span className="lp-nav-icon">✎</span>
                AIブログ
              </button>
              <button
                type="button"
                className="lp-side-disabled"
                onClick={() => setSidebarNotice("お問い合わせ管理を使うには、先にサイトを公開してください。")}
              >
                <span className="lp-nav-icon">✉</span>
                お問い合わせ
              </button>
            </>
          )}
        </nav>
        {sidebarNotice && <div className="lp-sidebar-notice">{sidebarNotice}</div>}
        <div className="lp-support-card">
          <div className="lp-support-illust">◆</div>
          <strong>困ったときは</strong>
          <span>サポートにご相談ください</span>
          <a href="mailto:ikko.teramoto@pascaline.jp" className="btn btn-ghost btn-sm">
            サポートに連絡する
          </a>
        </div>
      </aside>
      <div className="lp-workspace">
        <header className="lp-topbar">
          <div className="lp-office-switch">
            <span className="lp-office-icon">▥</span>
            <strong>制作中の事務所</strong>
          </div>
          <div className="lp-topbar-actions">
            <span className="lp-round-icon">?</span>
            <span className="lp-user-avatar">{initial}</span>
            <div className="lp-user-meta">
              <strong>{user ? `${user.last_name} ${user.first_name}`.trim() || "ユーザー" : "ユーザー"}</strong>
              <span>{user?.email}</span>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={onLogout}>
              ログアウト
            </button>
          </div>
        </header>
        <main className="lp-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// 運営(admin)エリアの共通レイアウト。
export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="app-shell admin">
      <header className="app-header admin-header">
        <Link to="/admin" className="brand">
          {OPERATOR.serviceName} <span className="badge">運営</span>
        </Link>
        <nav className="app-nav">
          <NavLink to="/admin" end>
            案件一覧
          </NavLink>
          <NavLink to="/admin/deploy">デプロイ管理</NavLink>
          <NavLink to="/admin/billing">請求・入金</NavLink>
          <NavLink to="/admin/credits">クレジット</NavLink>
        </nav>
        <div className="app-header-right">
          {user && <span className="user-email">{user.email}</span>}
          <button className="btn btn-ghost" onClick={onLogout}>
            ログアウト
          </button>
        </div>
      </header>
      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-links">
        <Link to="/terms">利用規約</Link>
        <Link to="/privacy">プライバシーポリシー</Link>
        <Link to="/tokushoho">特定商取引法に基づく表記</Link>
        <a href="https://pascaline.jp" target="_blank" rel="noreferrer">
          会社概要
        </a>
      </div>
      <div className="footer-copy">
        © {new Date().getFullYear()} {OPERATOR.companyName}
      </div>
    </footer>
  );
}
